import { z } from 'zod';
import { UuidSchema } from '../_shared/ids.js';
import { ArticleBodySchema } from './article-body.js';
import { ArticleCategoryRef } from './article.js';
import { AdPlacementSchema, AdTypeSchema, PlantillaBloqueSchema } from './enums.js';
import { MaquetaPortadaSchema } from './maqueta-portada.js';
import { PORTADA_POSICIONES } from './orden-notas.js';
import { PublicImage } from './public-image.js';
import { SiteBranding } from './site.js';
import { UrlKeySchema } from './url-key.js';
/**
 * Lo que la API pública le sirve al sitio (`apps/web-client`). Son contratos de **lectura
 * anónima**: sin ids de panel, sin estados de borrador, sin nada que el lector no deba ver.
 *
 * Dos reglas que atraviesan todo el archivo:
 *
 *  - **Los avisos se resuelven en el servidor**: una ubicación, un aviso (el de mayor
 *    `priority` entre los activos y en fecha). Si una ubicación no tiene campaña, la clave
 *    **no viene** y el sitio no dibuja nada — ni contenedor, ni alto reservado en vano.
 *  - **El shell viaja en cada respuesta**: cabecera y pie (marca, categorías con conteo,
 *    redes) se necesitan en toda página SSR, y un solo round-trip por request pesa menos
 *    que dos endpoints con caché coordinada.
 */
/* ───────────────────────────── notas ───────────────────────────── */
export const PublicCategoryRef = ArticleCategoryRef.extend({ urlKey: UrlKeySchema }).strict();
/** Una nota en cualquier lista del sitio: portada, bloques, categoría, búsqueda, "Más de". */
export const NotaPublicaCard = z
    .object({
    id: UuidSchema,
    title: z.string(),
    urlKey: UrlKeySchema,
    /** Ruta pública ya armada con `articlePath()`: el sitio no re-deriva año/mes. */
    path: z.string(),
    excerpt: z.string().nullable(),
    publishedAt: z.string().datetime(),
    /** Texto, no link: la página de autor quedó fuera de esta etapa. */
    author: z.string(),
    categories: z.array(PublicCategoryRef),
    /** Toda nota publicada tiene portada (requisito del producto): `null` solo ante datos rotos. */
    cover: PublicImage.nullable(),
})
    .strict();
/** Fila de la tira lateral de Última hora: hora + titular, sin foto. */
export const NotaLateral = z
    .object({
    id: UuidSchema,
    title: z.string(),
    path: z.string(),
    publishedAt: z.string().datetime(),
})
    .strict();
/* ───────────────────────────── avisos ───────────────────────────── */
export const PublicAd = z
    .object({
    id: UuidSchema,
    placement: AdPlacementSchema,
    type: AdTypeSchema,
    /** `IMAGE`: la pieza y su destino. */
    image: PublicImage.nullable(),
    targetUrl: z.string().nullable(),
    /** `HTML`: snippet de red publicitaria. El sitio lo aísla en un iframe con alto fijo. */
    htmlSnippet: z.string().nullable(),
})
    .strict();
/** Clave ausente = sin campaña activa = el espacio no se renderiza. */
export const PublicAds = z
    .object({
    HEADER: PublicAd,
    SIDEBAR: PublicAd,
    IN_FEED: PublicAd,
    IN_ARTICLE: PublicAd,
    FOOTER: PublicAd,
})
    .partial()
    .strict();
/* ───────────────────────────── shell ───────────────────────────── */
/**
 * Cuántas secciones entran en la fila de escritorio de la cabecera; el resto vive en el cajón, atrás
 * de "Más".
 *
 * El tope es **por corte y no por medición**: medir el ancho con JS causaría un salto al hidratar.
 * Está en los contracts porque lo necesitan los dos lados — el sitio para cortar, y el panel para
 * avisarle a la redacción que lo que marcó de más va a terminar en "Más" en vez de en la fila.
 */
export const PUBLIC_SECCIONES_EN_FILA = 6;
/**
 * Una sección en la navegación del sitio: las categorías del medio, más Última hora y Más leídas, que
 * también tienen página propia.
 *
 * **Una sola lista para las cuatro navegaciones** (fila de la cabecera, cajón, pie y el "Recorré por
 * sección" de la búsqueda). Cada una filtra lo que le toca, pero el orden y los nombres salen de acá:
 * dos fuentes serían dos menús que pueden decir cosas distintas.
 */
export const NavSeccion = z
    .object({
    /** Estable dentro del medio: el id del bloque. Sirve de `key` de React. */
    key: z.string(),
    name: z.string(),
    /** Ruta ya armada por el servidor (`/turismo`, `/ultima-hora`): el sitio no deriva URLs. */
    path: z.string(),
    /**
     * Notas publicadas. El cajón lo muestra al lado del nombre.
     *
     * `null` en Última hora y Más leídas: contar "todas las del medio" al lado de una categoría con 12
     * invitaría a compararlas, y en Más leídas el número que importaría son lecturas, no notas.
     */
    articleCount: z.number().int().min(0).nullable(),
    /** La eligió la redacción para la fila de la cabecera (ver `PUBLIC_SECCIONES_EN_FILA`). */
    enElHeader: z.boolean(),
})
    .strict();
export const SocialLinkPublico = z
    .object({
    key: z.string(),
    name: z.string(),
    /** SVG embebido del catálogo global; el sitio lo inyecta tal cual, en `currentColor`. */
    icon: z.string(),
    url: z.string(),
})
    .strict();
/** Lo que necesitan cabecera y pie en toda página. */
export const PublicShell = z
    .object({
    tenant: SiteBranding,
    /**
     * Las secciones del sitio, en el orden de sus bloques en la página principal: la navegación
     * cuenta la misma historia que la portada.
     *
     * Viajan **todas**, con `enElHeader` diciendo cuáles eligió la redacción para la fila de la
     * cabecera. La lista completa la necesitan el cajón y el pie igual, así que mandar solo las
     * marcadas ahorraría unos bytes y obligaría a un segundo viaje para el resto.
     */
    nav: z.array(NavSeccion),
    socialLinks: z.array(SocialLinkPublico),
    /**
     * El rótulo del bloque de Última hora, que el panel puede cambiar. Viaja en el shell y no en el
     * bloque porque la tira lateral está en las cuatro pantallas (home, nota, categoría y
     * búsqueda) y solo la home recibe los bloques.
     */
    ultimaHoraNombre: z.string(),
    /** Idem para el bloque de Más leídas: lo usa la tira lateral de `/ultima-hora`. */
    masLeidasNombre: z.string(),
})
    .strict();
/* ───────────────────────────── home ───────────────────────────── */
export const PublicPortadaSlot = z
    .object({
    /** 1 es la principal. */
    position: z.number().int().min(1).max(PORTADA_POSICIONES),
    /** `null` = hueco. La portada es curada: los huecos no se autocompletan. */
    nota: NotaPublicaCard.nullable(),
})
    .strict();
/**
 * Cuántas notas trae un bloque de categoría **que no eligió cantidad**: es una página de la
 * plantilla más grande (la lista, 6). El bloque que sí eligió manda su propio número, que sale de
 * `plantillas.ts` y llega hasta 24.
 */
export const PUBLIC_BLOQUE_CATEGORIA_NOTAS = 6;
/**
 * Tope de Última hora. El lateral de escritorio no corta por cantidad sino por alto (CSS
 * `overflow: hidden` a la altura del cuerpo): se pide holgado y sobra.
 */
export const PUBLIC_ULTIMA_HORA_TOPE = 30;
/**
 * Cuántas notas trae "Más leídas" **si el editor no eligió cantidad**: una página de lista.
 *
 * Constante propia y no `PUBLIC_BLOQUE_CATEGORIA_NOTAS`, aunque hoy sean el mismo 6: son dos
 * decisiones de producto distintas y cambiar el default de las secciones no tiene por qué cambiar el
 * del ranking. El editor que sí elige manda su número, que sale de `plantillas.ts`.
 */
export const PUBLIC_MAS_LEIDAS_NOTAS = 6;
/**
 * Cuánto hacia atrás mira el ranking.
 *
 * **Tiene que haber una ventana**: un contador desde siempre haría que la nota más vieja gane para
 * siempre y el bloque no cambiaría nunca. 48 h y no 24 porque un medio que publica pocas notas por
 * día se queda sin ranking un domingo tranquilo, y un bloque que aparece y desaparece se lee como
 * un error del sitio.
 */
export const PUBLIC_MAS_LEIDAS_VENTANA_HORAS = 48;
/**
 * Un bloque de la home, ya resuelto. La home los itera en su `position` **sin conocer sus
 * nombres**: la cantidad y el orden los decide el panel, y un bloque de categoría sin notas
 * publicadas directamente no viaja (su página propia sí existe, con estado vacío).
 */
export const BloquePublico = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('PORTADA'),
        id: UuidSchema,
        name: z.string(),
        position: z.number().int().min(0),
        /** Con qué maqueta se dibuja el bloque (ver `maqueta-portada.ts`). */
        maqueta: MaquetaPortadaSchema,
        /**
         * Solo las posiciones que la maqueta **dibuja**: las que quedaron guardadas fuera de ella no
         * viajan al sitio. El bloque no llega nunca vacío del todo —si la maqueta B se queda sin su
         * única nota, el servidor no manda el bloque.
         */
        slots: z.array(PublicPortadaSlot),
    })
        .strict(),
    z
        .object({
        kind: z.literal('ULTIMA_HORA'),
        id: UuidSchema,
        name: z.string(),
        position: z.number().int().min(0),
        notas: z.array(NotaPublicaCard),
    })
        .strict(),
    z
        .object({
        kind: z.literal('MAS_LEIDAS'),
        id: UuidSchema,
        name: z.string(),
        position: z.number().int().min(0),
        /**
         * Ordenadas de más leída a menos y, **entre iguales, la más reciente primero** — sea el
         * empate en 5 lecturas o en ninguna. Las posiciones que el ranking no llena las completan las
         * últimas publicadas, así el bloque no queda a medio dibujar; pero si **ninguna** nota tiene
         * lecturas el bloque no viaja, y de eso se encarga la API.
         *
         * El sitio las dibuja **numeradas**: acá el orden significa algo, que es lo que justifica el
         * 1, 2, 3 que no llevan los demás bloques.
         */
        notas: z.array(NotaPublicaCard),
        /** Igual que en un bloque de categoría: lo elegido, o `null` para la rotación del sitio. */
        plantilla: PlantillaBloqueSchema.nullable(),
    })
        .strict(),
    z
        .object({
        kind: z.literal('CATEGORIA'),
        id: UuidSchema,
        name: z.string(),
        position: z.number().int().min(0),
        categoryId: UuidSchema,
        categoryUrlKey: UrlKeySchema,
        notas: z.array(NotaPublicaCard),
        /**
         * La disposición que eligió el editor, o `null` para que la resuelva la rotación automática
         * del sitio. La **cantidad no viaja**: `notas` ya llega recortada, y de su largo sale cuántas
         * páginas dibuja el bloque (`paginasDelBloque`).
         */
        plantilla: PlantillaBloqueSchema.nullable(),
    })
        .strict(),
]);
export const PublicHomeResponse = z
    .object({
    shell: PublicShell,
    bloques: z.array(BloquePublico),
    ads: PublicAds,
})
    .strict();
/* ───────────────────────────── nota ───────────────────────────── */
export const PublicNota = z
    .object({
    id: UuidSchema,
    title: z.string(),
    urlKey: UrlKeySchema,
    path: z.string(),
    excerpt: z.string().nullable(),
    body: ArticleBodySchema,
    publishedAt: z.string().datetime(),
    author: z.string(),
    categories: z.array(PublicCategoryRef),
    cover: PublicImage.nullable(),
    seo: z
        .object({
        metaTitle: z.string().nullable(),
        metaDescription: z.string().nullable(),
        ogImage: PublicImage.nullable(),
        canonicalUrl: z.string().nullable(),
    })
        .strict(),
})
    .strict();
/** Cuántas notas lleva el pie "Más de <categoría>". */
export const PUBLIC_MAS_DE_NOTAS = 4;
export const PublicArticleResponse = z
    .object({
    shell: PublicShell,
    nota: PublicNota,
    /** Últimas de la primera categoría de la nota. `null` si la nota no tiene categoría. */
    masDe: z
        .object({ category: PublicCategoryRef, notas: z.array(NotaPublicaCard) })
        .strict()
        .nullable(),
    /** Tira lateral de escritorio (≥1200). */
    ultimaHora: z.array(NotaLateral),
    ads: PublicAds,
})
    .strict();
/* ─────────────────────── categoría y búsqueda ─────────────────────── */
export const PUBLIC_CATEGORIA_PAGE_SIZE = 12;
/** `?page=` de los listados públicos. El tamaño de página no es negociable desde afuera. */
export const PublicPageQuery = z
    .object({ page: z.coerce.number().int().min(1).default(1) })
    .strict();
export const PublicCategoryResponse = z
    .object({
    shell: PublicShell,
    category: z
        .object({
        id: UuidSchema,
        name: z.string(),
        urlKey: UrlKeySchema,
        description: z.string().nullable(),
    })
        .strict(),
    /** Listado uniforme y cronológico: la jerarquía es cosa de la portada. */
    items: z.array(NotaPublicaCard),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
    ultimaHora: z.array(NotaLateral),
    ads: PublicAds,
})
    .strict();
/* ─────────────── las secciones que no son categorías ─────────────── */
/**
 * Hasta dónde pagina la página de "Más leídas".
 *
 * Un ranking tiene cola: pasadas unas decenas de notas, la diferencia entre el puesto 40 y el 41 es
 * una lectura y no significa nada. El tope corta ahí en vez de fingir un orden que no existe, y de
 * paso acota la consulta del ranking, que ya viene con su propio cap de candidatas.
 */
export const PUBLIC_MAS_LEIDAS_PAGINA_MAX = 48;
/**
 * La respuesta de una sección que **no** es una categoría: `/ultima-hora` y `/mas-leidas`.
 *
 * Misma forma que `PublicCategoryResponse` —listado uniforme, paginado y lateral— porque para el
 * lector son la misma clase de página. La diferencia es que el título y la bajada no salen de una
 * fila de `categories`: los arma el servidor, con el rótulo que la redacción le puso al bloque.
 */
export const PublicSeccionResponse = z
    .object({
    shell: PublicShell,
    /** El rótulo del bloque, que la redacción puede cambiar ("Última hora" → "Lo último"). */
    titulo: z.string(),
    /** Una línea que explica qué se está mirando. La escribe el servidor, no la redacción. */
    descripcion: z.string(),
    items: z.array(NotaPublicaCard),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    /**
     * Cuántas hay en total. En Más leídas es **cuántas tienen lecturas** (con el tope de
     * `PUBLIC_MAS_LEIDAS_PAGINA_MAX`), no cuántas publicó el medio: acá no se rellena por fecha como
     * en el bloque de la portada, así que el total es el largo real del ranking.
     */
    total: z.number().int().min(0),
    ultimaHora: z.array(NotaLateral),
    /**
     * El ranking para la tira lateral, y **solo viene poblado en `/ultima-hora`**: ahí la lateral de
     * Última hora repetiría, fila por fila, lo que el lector ya tiene en el cuerpo. En `/mas-leidas`
     * pasa al revés, así que esa página se queda con `ultimaHora` y esto llega vacío.
     *
     * Vacío también cuando nadie leyó nada: la columna no se dibuja, que es preferible a rellenarla
     * con las últimas publicadas — serían otra vez las mismas del cuerpo.
     */
    masLeidas: z.array(NotaLateral),
    ads: PublicAds,
})
    .strict();
export const PUBLIC_SEARCH_Q_MAX = 120;
/** `q` vacía es válida: la pantalla de búsqueda sin término igual necesita shell y lateral. */
export const PublicSearchQuery = z
    .object({
    q: z.string().trim().max(PUBLIC_SEARCH_Q_MAX).default(''),
    page: z.coerce.number().int().min(1).default(1),
})
    .strict();
export const PublicSearchResponse = z
    .object({
    shell: PublicShell,
    q: z.string(),
    items: z.array(NotaPublicaCard),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
    ultimaHora: z.array(NotaLateral),
    ads: PublicAds,
})
    .strict();
/* ───────────────────────────── 404 ───────────────────────────── */
/** Datos de la página de error: la salida son la portada, la búsqueda y las últimas. */
export const PublicNotFoundResponse = z
    .object({
    shell: PublicShell,
    ultimas: z.array(NotaPublicaCard),
    ads: PublicAds,
})
    .strict();
/* ───────────────────────────── sitemap ───────────────────────────── */
/** Más que esto pide un sitemap index; queda fuera de esta etapa y anotado acá. */
export const PUBLIC_SITEMAP_MAX = 5000;
export const PublicSitemapEntry = z
    .object({
    path: z.string(),
    lastmod: z.string().datetime(),
})
    .strict();
export const PublicSitemapResponse = z
    .object({ entries: z.array(PublicSitemapEntry) })
    .strict();
/* ───────────────────────────── errores ───────────────────────────── */
export const PUBLIC_SITE_ERROR_CODES = {
    notaNoEncontrada: 'PUBLIC_NOTA_NO_ENCONTRADA',
    categoriaNoEncontrada: 'PUBLIC_CATEGORIA_NO_ENCONTRADA',
};
